import type { SolfegeSystemName } from 'shared/definitions/musicTheory/solfegeAndScaleDegrees';
import type { ModeName } from 'shared/definitions/musicTheory/scales';
import { wait } from 'shared/src/utils/wait';
import KeySignature from '../../uTheory.Notation/keySignature';
import utils from '../../utils';
import { getSolfegeSystemsMapForScaleMode } from '../getSolfegeSystemsMapForScaleMode';
import type {
  InteractiveOptions,
  LayoutItem,
  LayoutSettings,
  UserData,
} from '../interactivePrototype';
import InteractivePrototype from '../interactivePrototype';
import SolfegeMatchingSvelte from './SolfegeSDMultipleChoice.svelte';
import { Resolution } from '../../uTheory.Notation/resolution';
import type { ScaleDegree, SolfegePresentationSystem } from '../solfegeDefinitions';

type TrainableSkills =
  | 'major_scale_solfege'
  | 'major_scale_hand_signs'
  | 'natural_minor_scale_solfege'
  | 'natural_minor_scale_hand_signs'
  | 'harmonic_minor_scale_solfege'
  | 'harmonic_minor_scale_hand_signs'
  | 'melodic_minor_scale_solfege'
  | 'melodic_minor_scale_hand_signs';

interface SolfegeScaleDegreeHandSignMatchingOptions extends InteractiveOptions {
  /* Question type to ask: */
  presentationSystemForQuestions: SolfegePresentationSystem;
  /* The answer mode to present */
  presentationSystemForAnswers: SolfegePresentationSystem;
  /** Defaults to user-defined system */
  solfegeSystem?: SolfegeSystemName;
  /** Default: 'Major' */
  mode?: ModeName;
}

interface SolfegeScaleDegreeHandSignMatchingLayout extends LayoutSettings {
  svelteDiv: LayoutItem;
}

type SolfegeScaleDegreeHandSignMultipleChoiceQuestion = {
  scaleDegree: ScaleDegree;
  skills: TrainableSkills[];
};

/** 3 beats at tempo = 100 */
const rightAnswerDelay = 1800;

/**
 * Solfege, Scale Degree and Hand Sign Multiple Choice
 *
 * "What scale degree is ___?"
 */
export default class SolfegeScaleDegreeHandSignMultipleChoice extends InteractivePrototype {
  declare currentQuestion: SolfegeScaleDegreeHandSignMultipleChoiceQuestion;

  declare layout: SolfegeScaleDegreeHandSignMatchingLayout;

  declare options: SolfegeScaleDegreeHandSignMatchingOptions;

  declare questions: SolfegeScaleDegreeHandSignMultipleChoiceQuestion[];

  public solfegeSystem: SolfegeSystemName;

  private svelteDiv: HTMLDivElement;

  private gameBoard: SolfegeMatchingSvelte;

  private key: KeySignature;

  private resolution: Resolution;

  constructor(
    displayDiv: HTMLDivElement,
    options: SolfegeScaleDegreeHandSignMatchingOptions,
    userData: UserData
  ) {
    // Verify options:
    if (!options.presentationSystemForAnswers) {
      throw Error('presentationSystemForAnswers is required on options.');
    }
    if (!options.presentationSystemForQuestions) {
      throw Error('presentationSystemForQuestions is required on options.');
    }

    options = {
      drill: true,
      solfegeSystem: 'solfegeDoMinor',
      mode: 'Major',
      ...options,
    };

    super(displayDiv, options, userData);
    this.init(displayDiv, options, userData);
  }

  init(
    displayDiv: HTMLDivElement,
    options: SolfegeScaleDegreeHandSignMatchingOptions,
    userData: UserData
  ) {
    this.settings = {
      instructions: false,
      checkMark: true,
      player: true,
      progressTracker: true,
    };

    const quality = options.mode === 'Major' ? 'Major' : 'minor';
    this.key = new KeySignature(~~(Math.random() * 12) - 6, quality);
    console.log('this.key', this.key);
    this.solfegeSystem = options.solfegeSystem
      ? options.solfegeSystem
      : userData?.preferences.solfegeMethod || 'solfegeDoMinor';
    return super.init(displayDiv, options, userData);
  }

  displayInstructions() {
    if (this.questionNumber === 0) {
      this.resolution = new Resolution(this.player);
    }
  }

  async playAnswer() {
    await this.resolution.resolve(this.key, this.currentQuestion.scaleDegree, { tempo: 100 });
  }

  createCustomDivs() {
    this.svelteDiv = document.createElement('div');
    this.gameBoard = new SolfegeMatchingSvelte({
      target: this.svelteDiv,
      props: {
        interactive: this,
      },
    });
    this.displayDiv.appendChild(this.svelteDiv);

    this.layout.svelteDiv = {
      div: this.svelteDiv,
      visible: true,
      percent: 100,
      callback: this.gameBoard.resize,
    };
  }

  generateQuestions() {
    console.log('generating questions');
    const { scaleDegrees } = getSolfegeSystemsMapForScaleMode(
      this.options.mode || 'Major',
      this.solfegeSystem
    );
    this.questions = utils.shuffle(
      scaleDegrees.map((d) => ({ scaleDegree: d.name, skills: this.skills }))
    );
  }

  newQuestion() {
    super.newQuestion();
    this.gameBoard.newQuestion(this.currentQuestion.scaleDegree);
  }

  private get skills(): TrainableSkills[] {
    const skills: TrainableSkills[] = [];
    if (this.options.mode === 'Major') {
      if (
        this.options.presentationSystemForAnswers === 'solfege' ||
        this.options.presentationSystemForQuestions === 'solfege'
      ) {
        skills.push('major_scale_solfege');
      }
      if (
        this.options.presentationSystemForAnswers === 'handSigns' ||
        this.options.presentationSystemForQuestions === 'handSigns'
      ) {
        skills.push('major_scale_hand_signs');
      }
    }
    if (this.options.mode === 'naturalMinor') {
      if (
        this.options.presentationSystemForAnswers === 'solfege' ||
        this.options.presentationSystemForQuestions === 'solfege'
      ) {
        skills.push('natural_minor_scale_solfege');
      }
      if (
        this.options.presentationSystemForAnswers === 'handSigns' ||
        this.options.presentationSystemForQuestions === 'handSigns'
      ) {
        skills.push('natural_minor_scale_hand_signs');
      }
    }
    if (this.options.mode === 'harmonicMinor') {
      if (
        this.options.presentationSystemForAnswers === 'solfege' ||
        this.options.presentationSystemForQuestions === 'solfege'
      ) {
        skills.push('harmonic_minor_scale_solfege');
      }
      if (
        this.options.presentationSystemForAnswers === 'handSigns' ||
        this.options.presentationSystemForQuestions === 'handSigns'
      ) {
        skills.push('harmonic_minor_scale_hand_signs');
      }
    }
    if (
      this.options.mode === 'melodicMinorAscending' ||
      this.options.mode === 'melodicMinorDescending'
    ) {
      if (
        this.options.presentationSystemForAnswers === 'solfege' ||
        this.options.presentationSystemForQuestions === 'solfege'
      ) {
        skills.push('melodic_minor_scale_solfege');
      }
      if (
        this.options.presentationSystemForAnswers === 'handSigns' ||
        this.options.presentationSystemForQuestions === 'handSigns'
      ) {
        skills.push('melodic_minor_scale_hand_signs');
      }
    }
    return skills;
  }

  // newQuestion() {
  //   super.newQuestion();
  // }

  // displayInstructions() {
  //   super.displayInstructions();
  // }

  checkAnswer(studentAnswer: ScaleDegree) {
    if (this.rightAnswerDelay) return undefined;
    studentAnswer = studentAnswer === '8' ? '1' : studentAnswer;
    const correctScaleDegree =
      this.currentQuestion.scaleDegree === '8' ? '1' : this.currentQuestion.scaleDegree;
    const correct = studentAnswer === correctScaleDegree;
    if (this.options.testMode) {
      return this.checkpointAnswer(correct);
    }
    console.log('question skills', this.currentQuestion.skills);

    // When correct, call rightAnswer();
    if (correct) {
      this.gameBoard.$set({
        disabled: true,
      });
      wait(rightAnswerDelay / 1000).then(() => {
        this.gameBoard.$set({
          disabled: false,
        });
      });
      return this.rightAnswer(rightAnswerDelay);
    }
    // When incorrect, call wrongAnswer();
    return this.wrongAnswer();
  }

  gameOver() {
    this.gameBoard.$set({ gameOver: true });
    return super.gameOver();
  }

  exit() {
    this.gameBoard.$destroy();
    super.exit();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: any) {
    console.error(questionString);
    throw Error('Checkpoint mode not implemented for solfegeScaleDegreeHandSignMultipleChoice');
    return Promise.reject('Checkpoint mode not implemented');
  }

  static registry: {
    description: string;
    skills: TrainableSkills[];
    options: SolfegeScaleDegreeHandSignMatchingOptions;
  }[] = [
    {
      description: 'Hand Sign Identification (Major)',
      skills: ['major_scale_solfege', 'major_scale_hand_signs'],
      options: {
        presentationSystemForQuestions: 'handSigns',
        presentationSystemForAnswers: 'solfege',
        mode: 'Major',
      },
    },
    {
      description: 'Hand Sign Identification (Natural Minor)',
      skills: ['natural_minor_scale_solfege', 'natural_minor_scale_hand_signs'],
      options: {
        presentationSystemForQuestions: 'handSigns',
        presentationSystemForAnswers: 'solfege',
        mode: 'naturalMinor',
      },
    },
    {
      description: 'Hand Sign Identification (Harmonic Minor)',
      skills: ['harmonic_minor_scale_solfege', 'harmonic_minor_scale_hand_signs'],
      options: {
        presentationSystemForQuestions: 'handSigns',
        presentationSystemForAnswers: 'solfege',
        mode: 'harmonicMinor',
      },
    },
    {
      description: 'Hand Sign Identification (Melodic Minor)',
      skills: ['melodic_minor_scale_solfege', 'melodic_minor_scale_hand_signs'],
      options: {
        presentationSystemForQuestions: 'handSigns',
        presentationSystemForAnswers: 'solfege',
        mode: 'melodicMinorAscending',
      },
    },
  ];
}
